import { SearchableSelect } from '@/components/ui/searchable-select'
import ComposioAppIcon from '@/frontend/components/Integrations/ComposioAppIcon'
import type {
  OrganizationAuthConfigCopy,
  OrganizationAuthConfigIntegration,
} from '@/frontend/pages/CommandCenter/Organizations/components/organizationAuthConfigTypes'

interface Props {
  integration: OrganizationAuthConfigIntegration
  copy: OrganizationAuthConfigCopy
  disabled: boolean
  onChange: (authConfigId: string) => void
}

function configName(
  integration: OrganizationAuthConfigIntegration,
  id: string | null,
  copy: OrganizationAuthConfigCopy,
) {
  if (!id) return copy.automatic
  return integration.authConfigs.find((config) => config.id === id)?.name || copy.unavailable
}

export default function OrganizationAuthConfigRow({ integration, copy, disabled, onChange }: Props) {
  const overrideId = integration.organizationAuthConfigId ?? ''
  const overrideMissing = overrideId !== '' && !integration.authConfigs.some((config) => config.id === overrideId)
  const options = integration.authConfigs.map((config) => ({
    value: config.id,
    label: `${config.name} · ${config.authScheme ?? '—'} · ${config.id}`,
  }))

  if (overrideMissing) {
    options.unshift({ value: overrideId, label: `${copy.unavailable} · ${overrideId}` })
  }

  const globalName = configName(integration, integration.globalAuthConfigId, copy)
  const effectiveName = configName(integration, integration.effectiveAuthConfigId, copy)
  const status = overrideId ? `${copy.selected}: ${effectiveName}` : `${copy.inherited}: ${globalName}`
  const selectorDisabled = disabled || (integration.authConfigs.length === 0 && !overrideMissing)

  return (
    <div className="grid gap-3 border-t border-border px-4 py-4 first:border-t-0 lg:grid-cols-[minmax(180px,0.75fr)_minmax(320px,1.4fr)] lg:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <ComposioAppIcon logo={integration.logo} name={integration.name} className="h-9 w-9" />
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{integration.name}</p>
          <p className="truncate font-mono text-xs text-muted-foreground">{integration.slug}</p>
        </div>
      </div>
      <div className="space-y-1.5">
        <SearchableSelect
          options={options}
          value={overrideId}
          onValueChange={onChange}
          placeholder={`${copy.globalDefault}: ${globalName}`}
          searchPlaceholder={copy.searchPlaceholder}
          ariaLabel={`${copy.selectLabel} ${integration.name}`}
          clearLabel={`${copy.globalDefault}: ${globalName}`}
          emptyStateLabel={copy.empty}
          disabled={selectorDisabled}
        />
        <div className="flex min-w-0 items-center gap-2 px-0.5 text-[11px] text-muted-foreground">
          <span className="truncate">{status}</span>
          {integration.effectiveAuthConfigId && (
            <code className="ml-auto shrink-0 text-[10px]">{integration.effectiveAuthConfigId}</code>
          )}
        </div>
      </div>
    </div>
  )
}
